/**
 * QA Panel — Environment controller.
 *
 * Lets QA pick a casino + fill launch params and open the deployed
 * game-client URL for that casino in a new window. The lobby is then
 * embedded inside that game-client (matching real QA traffic), as opposed
 * to launching another instance of the lobby standalone.
 *
 * The CASINOS list is built from `./casinos.json` — the shared source of
 * truth also consumed (via fs, at launch time) by
 * `scripts/casino-launcher/casino-launcher.js`. Edit the JSON, not this file.
 *
 * The controller holds no live subscriptions and does no polling. State is
 * read on demand from appStore + window.location; init/destroy are
 * effectively no-ops aside from caching the onChange callback for
 * future-proofing (mirrors the SentryDebugController shape).
 *
 * The actual "launch" happens in the popup via window.open — the controller
 * never issues the navigation itself, so there is no launch method.
 */

import { useAppStore } from '../../../store/app/appStore';
import casinosJson from './casinos.json';

/* ── Public state shape sent to the popup ───────────────────────── */

export interface EnvironmentCasino {
    /** Stable identifier — used as `casinoId` query param + button key. */
    id: string;
    /** Human-friendly label rendered above the id on the casino button. */
    label: string;
    /** Game-server websocket host (no protocol prefix). */
    host: string;
    /** Currency code passed to the lobby boot params. */
    currencyCode: string;
    /**
     * Deployed game-client URL for this casino. Derived from `host` at
     * module-init time by `deriveGameClientUrl`: swap `-game-server-` for
     * `-game-client-`, strip `/game-websocket`, prepend `https://`,
     * append `/`. One source of truth — don't drift the two fields apart.
     *
     * Expected values:
     *   EGTNEW → https://egtnew-game-client-ong-qa-ext.egt-ong.com/
     *   9999   → https://gcdevcasino1-game-client-ong-qa-ext.egt-ong.com/
     *   EGTBG  → https://casino3-game-client-ong-qa-ext.egt-ong.com/
     *   55555  → https://gcdevcasino2-game-client-ong-qa-ext.egt-ong.com/
     */
    gameClientUrl: string;
    /** Default value the popup pre-fills into the playerId input when no live id. */
    defaultPlayerId: string;
}

/**
 * Experimental game-client environments. Distinct category — these point at
 * the same `game-client-experimental<N>.egt-ong.com` deployments QA uses for
 * isolated game testing. They take only a `gameKey` query param, nothing else.
 */
export interface ExperimentalEnv {
    /** Stable identifier — `experimental2` … `experimental7`. */
    id: string;
    /** Short label rendered on the pill (just the number — heading provides context). */
    label: string;
    /** Deployed experimental game-client URL, already ending with a trailing slash. */
    gameClientUrl: string;
}

export interface EnvironmentState {
    casinos: EnvironmentCasino[];
    experimentals: ExperimentalEnv[];
    /** Current session's playerId (from appStore.settings.playerId). */
    currentPlayerId: string | null;
    /** Current session's playerName, carried forward from the lobby URL. */
    currentPlayerName: string | null;
    /** Current session's language (URL `language` param). Default `'en'`. */
    currentLanguage: string;
    /** Current session's gameId (URL `gameId` param). Default `''`. */
    currentGameId: string;
    /** Current session's multiplayer flag (URL `multiplayer` param). Default `'false'`. */
    currentMultiplayer: string;
    /** Current session's demo flag (URL `demo` param). Default `'false'`. */
    currentDemo: string;
}

/* ── Static casino catalog ──────────────────────────────────────── */

/**
 * Derive the deployed game-client URL from a game-server websocket host.
 * Pattern: `<prefix>-game-server-<env>.egt-ong.com/game-websocket`
 *       →  `https://<prefix>-game-client-<env>.egt-ong.com/`
 */
function deriveGameClientUrl(host: string): string {
    const stripped = host.replace(/\/game-websocket$/, '');
    const swapped = stripped.replace('-game-server-', '-game-client-');
    return `https://${swapped}/`;
}

/**
 * `casinos.json` carries launcher-only fields too (apiIntegrationHost,
 * comeFromLobby, defaultGameKey) — this tab uses only the subset picked here.
 */
const CASINOS: EnvironmentCasino[] = casinosJson.map(seed => ({
    id: seed.id,
    label: seed.label,
    host: seed.host,
    currencyCode: seed.currencyCode,
    defaultPlayerId: seed.defaultPlayerId,
    gameClientUrl: deriveGameClientUrl(seed.host),
}));

/**
 * Experimental envs 2–7. Single-purpose game-client deployments — QA targets
 * them by `gameKey` only; nothing else of the casino URL shape applies.
 * URL pattern: `https://game-client-experimental<N>.egt-ong.com/`.
 */
const EXPERIMENTAL_NUMBERS: ReadonlyArray<number> = [2, 3, 4, 5, 6, 7];
const EXPERIMENTALS: ExperimentalEnv[] = EXPERIMENTAL_NUMBERS.map(n => ({
    id: `experimental${n}`,
    label: String(n),
    gameClientUrl: `https://game-client-experimental${n}.egt-ong.com/`,
}));

/* ── Controller ─────────────────────────────────────────────────── */

export class EnvironmentController {
    private onChange: (() => void) | null = null;

    init(onChange: () => void): void {
        // No subscriptions or polling — the data is static + read on demand.
        // Hold the callback for future-proofing; matches the controller-shape
        // contract used by every other tab.
        this.onChange = onChange;
    }

    destroy(): void {
        this.onChange = null;
    }

    getState(): EnvironmentState {
        // Defensive read — same posture as the Sentry controller's tracker
        // reads. A transient throw inside appStore must not blank the tab.
        let currentPlayerId: string | null = null;
        try {
            const pid = useAppStore.getState().settings.playerId;
            currentPlayerId = typeof pid === 'string' && pid.length > 0 ? pid : null;
        } catch {
            currentPlayerId = null;
        }

        // Carry-forward params from the lobby's own URL. Sandboxed contexts
        // can throw on `window.location.search` access, so the whole block
        // is guarded and falls back to sensible defaults matching the
        // canonical launch URL (en/empty/false).
        let currentPlayerName: string | null = null;
        let currentLanguage = 'en';
        let currentGameId = '';
        let currentMultiplayer = 'false';
        let currentDemo = 'false';
        try {
            const search = new URLSearchParams(window.location.search);
            const pn = search.get('playerName');
            currentPlayerName = pn && pn.length > 0 ? pn : null;
            currentLanguage = search.get('language') || 'en';
            currentGameId = search.get('gameId') || '';
            currentMultiplayer = search.get('multiplayer') || 'false';
            currentDemo = search.get('demo') || 'false';
        } catch {
            // keep defaults
        }

        return {
            casinos: CASINOS,
            experimentals: EXPERIMENTALS,
            currentPlayerId,
            currentPlayerName,
            currentLanguage,
            currentGameId,
            currentMultiplayer,
            currentDemo,
        };
    }
}
